import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import { escapeContextLabel } from 'tripPlanner/utils/contextTag'
import { buildOfferContext, readCurrencyCode } from 'tripPlanner/utils/offerContext'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import {
  formatAssistantRoomOccupancyLines,
  type FormatAssistantRoomOccupancyTextOptions,
} from './assistantRoomOccupancyText'
import {
  isOccupancyThresholdOffer,
  resolveAssistantOccupancyAgeThresholds,
  type AssistantOccupancyAgeThresholds,
} from './assistantOccupancyAgeThresholds'

// ── Types ──

export interface MessageEnrichmentParams {
  message: string
  multiChoiceSelection: Array<string>
  sendMessageOptions?: SendMessageOptions
  mobileSelectionText: string | undefined
  offerContext: { propertyName: string; contextString: string } | undefined
  occupancyOffer?: App.Offer | App.BedbankOffer | App.BundleOffer
}

export interface BuildRoomOptionContextOptions {
  ageThresholds?: AssistantOccupancyAgeThresholds
}

export function resolveOccupancyAgeThresholdsForSendMessage(
  options: SendMessageOptions | undefined,
  offersById: Record<string, App.AnyOffer>,
): AssistantOccupancyAgeThresholds | undefined {
  const offer = options?.offerId ? offersById[options.offerId] : undefined
  const resolvedOffer = isOccupancyThresholdOffer(offer) ? offer : undefined

  return resolveAssistantOccupancyAgeThresholds({
    ageThresholds: options?.occupancyAgeThresholds,
    offer: resolvedOffer,
  })
}

// ── Offer helpers ──

export function getOfferPropertyName(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return offer.property.name
  }
  if (isLEHotel(offer)) {
    return offer.property?.name || offer.name
  }
  return offer.name
}

export function extractRoomPackages(
  offer: App.AnyOffer,
  currentCurrency: string,
): Array<{ name: string; price?: number; currencyCode?: string }> {
  if (isBedbankOffer(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.name || 'Room Package',
      price: pkg.sell?.price,
      currencyCode: readCurrencyCode(pkg.sell) ?? currentCurrency,
    }))
  }

  if (isLEHotel(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.roomType?.name || pkg.name || 'Room Package',
      price: pkg.price,
      currencyCode: readCurrencyCode(pkg) ?? currentCurrency,
    }))
  }

  return []
}

function getOfferDescription(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return ('description' in offer && offer.description) || offer.tagline || ''
  }
  if (isLEHotel(offer)) {
    return offer.description || ''
  }
  return ''
}

function getOfferFinePrint(offer: App.AnyOffer): string | undefined {
  if (isLEHotel(offer)) {
    return offer.finePrint
  }
  if (isBedbankOffer(offer)) {
    return offer.property?.finePrintOverride
  }
  return undefined
}

export function buildOfferContextString(
  offer: App.AnyOffer,
  currentCurrency: string,
): { propertyName: string; contextString: string } {
  const propertyName = getOfferPropertyName(offer)
  const description = getOfferDescription(offer)
  const roomPackages = extractRoomPackages(offer, currentCurrency)
  const finePrint = getOfferFinePrint(offer)
  const offerUrl = offerPageURL(offer)

  const contextString = buildOfferContext(
    offer,
    propertyName,
    description,
    roomPackages,
    finePrint,
    offerUrl,
    currentCurrency,
  )

  return { propertyName, contextString }
}

// ── Message composition ──

export function buildEnrichedMessage(params: MessageEnrichmentParams): string {
  const {
    message,
    multiChoiceSelection,
    sendMessageOptions,
    mobileSelectionText,
    offerContext,
  } = params

  // Combine multi-choice selection with user's typed message
  const selectionPart = multiChoiceSelection.length > 0
    ? multiChoiceSelection.join(', ')
    : ''
  let combined = selectionPart && message.trim()
    ? `${selectionPart}, ${message}`
    : selectionPart || message

  // Append LE offer ID when adding an offer to the trip
  if (sendMessageOptions?.offerId) {
    combined = `${combined}${buildOfferIdContext(sendMessageOptions.offerId, sendMessageOptions.offerType)}`
  }

  // Append room/package selection when a specific room option was chosen.
  // Two legitimate resolution tiers here, not a redundant re-resolution:
  // sendMessageOptions.occupancyAgeThresholds (caller-resolved, e.g. from the
  // Redux offersById cache) takes priority; params.occupancyOffer (typically
  // the React-Query-backed current offer) is the fallback when the caller
  // hasn't resolved thresholds yet. buildRoomOptionContext below only passes
  // this result straight through — it does not resolve a third time.
  if (sendMessageOptions?.roomOption) {
    combined = `${combined}${buildRoomOptionContext(sendMessageOptions.roomOption, {
      ageThresholds: resolveAssistantOccupancyAgeThresholds({
        ageThresholds: sendMessageOptions.occupancyAgeThresholds,
        offer: params.occupancyOffer,
      }),
    })}`
  }

  // Append mobile selection context
  if (mobileSelectionText) {
    combined = `${combined}\n<Context label="${escapeContextLabel(mobileSelectionText)}">The user highlighted the following text: ${mobileSelectionText}</Context>`
  }

  // Append offer context
  if (offerContext) {
    combined = `${combined}\n\n<Context label="${escapeContextLabel(offerContext.propertyName)}">${offerContext.contextString}</Context>`
  }

  return combined
}

export function buildOfferIdContext(offerId: string, offerType?: string): string {
  const offerTypeLine = offerType ? `\nOffer Type: ${offerType}` : ''
  const contextLabel = offerType === 'experience' ? 'LE Experience offer' : 'LE Hotel offer'
  return `\n<Context label="${contextLabel}">Offer ID: ${offerId}${offerTypeLine}</Context>`
}

export function buildRoomOptionContext(
  roomOption: HotelRoomOption,
  options?: BuildRoomOptionContextOptions,
): string {
  // ageThresholds is already fully resolved by the caller — pass through only.
  const occupancyFormatOptions: FormatAssistantRoomOccupancyTextOptions | undefined =
    options?.ageThresholds ? { ageThresholds: options.ageThresholds } : undefined
  const lines: Array<string> = []
  if (roomOption.checkIn) lines.push(`Check-in: ${roomOption.checkIn}`)
  if (roomOption.checkOut) lines.push(`Check-out: ${roomOption.checkOut}`)
  if (roomOption.packageId) lines.push(`Package ID: ${roomOption.packageId}`)
  if (roomOption.packageName) lines.push(`Package Name: ${roomOption.packageName}`)
  if (roomOption.durationInDays !== undefined) lines.push(`Package Duration: ${roomOption.durationInDays}`)
  if (roomOption.roomTypeId) lines.push(`Room Type ID: ${roomOption.roomTypeId}`)
  if (roomOption.roomTypeName) lines.push(`Room Type Name: ${roomOption.roomTypeName}`)
  if (roomOption.rateId) lines.push(`Room Rate ID: ${roomOption.rateId}`)
  // Charter bundle (e.g. Maldives direct): the user's chosen flight route/origin/fare.
  // Carried in the same add context as package/rate so the agent persists them
  // onto the new item at creation (svc-trip add_offer → charterFlightBundle).
  if (roomOption.charterBundleRoute) lines.push(`Charter Bundle Route: ${roomOption.charterBundleRoute}`)
  if (roomOption.charterOriginAirportCode) lines.push(`Charter Origin Airport Code: ${roomOption.charterOriginAirportCode}`)
  if (roomOption.charterFareClass) lines.push(`Charter Fare Class: ${roomOption.charterFareClass}`)
  if (roomOption.occupancies?.length) {
    lines.push(`Rooms: ${roomOption.occupancies.length}`)
    lines.push(...formatAssistantRoomOccupancyLines(roomOption.occupancies, occupancyFormatOptions))
  }
  return `\n<Context label="LE Hotel room option">${lines.join('\n')}</Context>`
}
